/** Kurum hesabı kaydı; hukuki teyitler API'de de zorunludur. */
import { useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api, ApiRequestError } from '@/lib/api';
import { Button, Checkbox, Field, Input } from '@/components/ui';
import { useI18n } from '@/lib/i18n';

const MIN_PASSWORD = 10;

function passwordStrength(value: string): { score: 0 | 1 | 2 | 3; label: 'tooShort' | 'weak' | 'good' | 'strong' } {
  if (value.length < MIN_PASSWORD) return { score: 0, label: 'tooShort' };
  let variety = 0;
  if (/[a-zçğıöşü]/.test(value)) variety++;
  if (/[A-ZÇĞİÖŞÜ]/.test(value)) variety++;
  if (/\d/.test(value)) variety++;
  if (/[^\w\s]/.test(value)) variety++;
  if (value.length >= 16 && variety >= 3) return { score: 3, label: 'strong' };
  if (value.length >= 12 && variety >= 2) return { score: 2, label: 'good' };
  return { score: 1, label: 'weak' };
}

export default function Register() {
  const [searchParams] = useSearchParams();
  const { language, t } = useI18n();
  const [form, setForm] = useState({ name: '', accountName: '', email: '', password: '', passwordConfirm: '', kvkkRead: false, termsAccepted: false });
  const [fields, setFields] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const strength = passwordStrength(form.password);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    const localFields: Record<string, string> = {};
    if (form.password !== form.passwordConfirm) localFields['passwordConfirm'] = t('auth.passwordMismatch');
    if (!form.kvkkRead) localFields['kvkkRead'] = t('auth.confirmPrivacyRead');
    if (!form.termsAccepted) localFields['termsAccepted'] = t('auth.confirmTerms');
    if (Object.keys(localFields).length > 0) {
      setFields(localFields);
      setError(t('auth.completeMarked'));
      return;
    }

    setError(null);
    setFields({});
    setBusy(true);
    try {
      await api.post('/auth/register', {
        name: form.name.trim(),
        email: form.email.trim(),
        password: form.password,
        passwordConfirm: form.passwordConfirm,
        kvkkRead: form.kvkkRead,
        termsAccepted: form.termsAccepted,
        accountName: form.accountName.trim(),
        language,
        sourceCatalog: searchParams.get('catalog') || undefined,
        sourceProductSlug: searchParams.get('product') || undefined,
        sourcePath: searchParams.get('source') || undefined,
      });
      setDone(true);
    } catch (err) {
      if (err instanceof ApiRequestError) {
        setError(err.message);
        setFields(err.fields ?? {});
      } else setError(t('auth.serverUnavailable'));
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return <>
      <div><h1 className="auth__title">{t('auth.checkEmail')}</h1><p className="auth__sub"><strong>{form.email}</strong> {t('auth.verificationSent')}</p></div>
      <div className="auth__ok">{t('auth.checkSpam')}</div>
      <Link className="btn btn--primary btn--lg auth__wide-action" to={`/eposta-dogrula?email=${encodeURIComponent(form.email)}`}>{language === 'tr' ? 'Doğrulama kodunu gir' : 'Enter verification code'}</Link>
    </>;
  }

  return <>
    <div><h1 className="auth__title">{t('auth.createOrg')}</h1><p className="auth__sub">{t('auth.createOrgSubtitle')}</p></div>
    <form className="auth__form" onSubmit={handleSubmit} noValidate>
      {error && <div className="auth__error" role="alert">{error}</div>}
      <div className="auth__form-grid">
        <Field label={t('auth.fullName')} required error={fields['name']}>{({ id, describedBy, invalid }) => <Input id={id} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} autoComplete="name" autoFocus required invalid={invalid} aria-describedby={describedBy} placeholder={t('auth.fullName')} />}</Field>
        <Field label={t('auth.organizationName')} required error={fields['accountName']}>{({ id, describedBy, invalid }) => <Input id={id} value={form.accountName} onChange={(e) => setForm({ ...form, accountName: e.target.value })} autoComplete="organization" required invalid={invalid} aria-describedby={describedBy} placeholder={t('auth.organizationName')} />}</Field>
      </div>
      <Field label={t('auth.email')} required error={fields['email']}>{({ id, describedBy, invalid }) => <Input id={id} type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} autoComplete="email" required invalid={invalid} aria-describedby={describedBy} placeholder="name@company.com" />}</Field>
      <div className="auth__form-grid auth__form-grid--passwords">
        <Field label={t('auth.password')} required error={fields['password']} hint={t('auth.passwordMin')}>{({ id, describedBy, invalid }) => <Input id={id} type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} autoComplete="new-password" required invalid={invalid} aria-describedby={describedBy} />}</Field>
        <Field label={t('auth.passwordAgain')} required error={fields['passwordConfirm']}>{({ id, describedBy, invalid }) => <Input id={id} type="password" value={form.passwordConfirm} onChange={(e) => setForm({ ...form, passwordConfirm: e.target.value })} autoComplete="new-password" required invalid={invalid} aria-describedby={describedBy} />}</Field>
      </div>
      {form.password.length > 0 && <div className="auth__strength" aria-live="polite"><span className="auth__strength-track"><span className={`auth__strength-fill auth__strength-fill--${strength.score}`} /></span><span>{strength.label === 'tooShort' ? t('auth.strengthTooShort') : strength.label === 'weak' ? t('auth.strengthWeak') : strength.label === 'good' ? t('auth.strengthGood') : t('auth.strengthStrong')}</span></div>}
      <div className="auth__legal-checks">
        <Checkbox checked={form.kvkkRead} onChange={(e) => setForm({ ...form, kvkkRead: e.target.checked })} aria-invalid={Boolean(fields['kvkkRead']) || undefined} label={<Link to="/kvkk-aydinlatma" target="_blank">{t('auth.privacyRead')}</Link>} />
        {fields['kvkkRead'] && <p className="field__error" role="alert">{fields['kvkkRead']}</p>}
        <Checkbox checked={form.termsAccepted} onChange={(e) => setForm({ ...form, termsAccepted: e.target.checked })} aria-invalid={Boolean(fields['termsAccepted']) || undefined} label={<Link to="/kullanim-kosullari" target="_blank">{t('auth.termsAccepted')}</Link>} />
        {fields['termsAccepted'] && <p className="field__error" role="alert">{fields['termsAccepted']}</p>}
      </div>
      <Button type="submit" variant="primary" size="lg" loading={busy}>{t('auth.createAccount')}</Button>
    </form>
    <p className="auth__switch">{t('auth.haveAccount')} <Link to="/giris">{t('auth.signIn')}</Link></p>
  </>;
}
